import { logClientError } from '@utils/server-log';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthFailureService } from '@services/auth-failure/auth-failure.service';
import { PermissionsService } from '@services/permissions/permissions.service';
import { SharedService } from '@services/shared/shared.service';
import { showWarningToast } from '@utils/toast-utils';
import { filter, map, Observable, take } from 'rxjs';

/**
 * Maps route base segments to feature keys used for `resource:read` access.
 */
const ROUTE_FEATURE_MAP: Record<string, string> = {
  '': 'subscriptions',
  'detalhes-da-assinatura': 'subscriptions',
  usuarios: 'users',
  conteudo: 'contents',
  configuracoes: 'settings',
  campanhas: 'campaigns',
  'transacoes-financeiras': 'transactions',
  relatorios: 'reports',
  atividades: 'activity',
  ajuda: 'help',
  'gerenciar-cargos': 'roles',
};

/**
 * Sub-rotas que são módulos independentes do seu segmento base.
 * Avaliadas ANTES do `ROUTE_FEATURE_MAP` — sem isto, `status-do-cartao` seria
 * liberada por `transactions:read` só por morar dentro de `transacoes-financeiras`.
 */
const SUBROUTE_FEATURE_MAP: Record<string, string> = {
  'transacoes-financeiras/status-do-cartao': 'cards',
};

/** Intervalo mínimo entre toasts de "sem acesso" para não inundar a tela. */
const DENIED_TOAST_COOLDOWN_MS = 3_000;

@Injectable({
  providedIn: 'root',
})
export class FeatureGuard {
  private lastDeniedToastAt = 0;

  constructor(
    private router: Router,
    private shared: SharedService,
    private permissionsService: PermissionsService,
    private authFailure: AuthFailureService
  ) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    return this.checkFeatureAccess(state);
  }

  canActivateChild(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    return this.checkFeatureAccess(state);
  }

  private checkFeatureAccess(
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    // Logout em andamento: não interfere na navegação para /login.
    if (this.authFailure.isFailing) {
      return false;
    }

    if (this.permissionsService.fetchedFromApi || this.permissionsService.hasCachedPermissions) {
      return this.validateAccess(state.url);
    }

    return this.permissionsService.permissions$.pipe(
      filter((permissions) => permissions !== undefined),
      take(1),
      map(() => this.validateAccess(state.url))
    );
  }

  private validateAccess(url: string): boolean | UrlTree {
    const { routeKey, feature: requiredFeature } = this.resolveRouteFeature(url);

    if (!requiredFeature) {
      return this.deny(url, routeKey, 'Rota não mapeada em ROUTE_FEATURE_MAP');
    }

    if (this.shared.hasFeatureAccess(requiredFeature)) {
      this.authFailure.resetRedirectTracking();
      return true;
    }

    return this.deny(
      url,
      routeKey,
      `Sem permissão de leitura para "${requiredFeature}"`,
      requiredFeature
    );
  }

  /**
   * Nega o acesso sem nunca deixar o usuário preso.
   *
   * Ordem de segurança:
   * 1. Sem home navegável -> logout (permissões vazias/inconsistentes).
   * 2. Home aponta para a própria rota negada -> logout (loop garantido).
   * 3. Excesso de redirects na janela -> logout (loop detectado).
   * 4. Caso normal -> toast + redirect para a home do usuário.
   */
  private deny(
    url: string,
    baseRoute: string,
    detail: string,
    requiredFeature?: string
  ): boolean | UrlTree {
    const homeRoute = this.shared.resolveUserHomeRoute();
    const diagnostics = {
      url,
      baseRoute,
      requiredFeature,
      detail,
      homeRoute,
      permissions: this.permissionsService.current,
      effectiveFeatures: this.shared.getEffectiveFeatures(),
      menuFeatures: this.shared.menuFeatures,
    };

    logClientError('[FeatureGuard] Acesso negado:', diagnostics);

    if (!homeRoute) {
      return this.authFailure.failUrlTree('no_permissions', {
        context: { step: 'FeatureGuard.deny', ...diagnostics },
      });
    }

    // A home resolvida é justamente a rota que acabou de ser negada:
    // redirecionar aqui produziria o loop guard -> home -> guard.
    if (this.getBaseRouteFromUrl(homeRoute) === baseRoute) {
      return this.authFailure.failUrlTree('inconsistent_permissions', {
        context: { step: 'FeatureGuard.deny (home == rota negada)', ...diagnostics },
      });
    }

    if (!this.authFailure.trackGuardRedirect(url, homeRoute)) {
      return this.authFailure.failUrlTree('redirect_loop', {
        context: { step: 'FeatureGuard.deny', ...diagnostics },
      });
    }

    this.notifyDenied(requiredFeature);
    return this.router.createUrlTree([homeRoute]);
  }

  private notifyDenied(requiredFeature?: string): void {
    const now = Date.now();
    if (now - this.lastDeniedToastAt < DENIED_TOAST_COOLDOWN_MS) {
      return;
    }
    this.lastDeniedToastAt = now;

    const suffix = requiredFeature ? ` (requer "${requiredFeature}:read")` : '';
    showWarningToast(
      `Você não tem acesso a esta página${suffix}. Redirecionamos para sua página inicial.`,
      'Acesso negado'
    );
  }

  /**
   * Resolve a feature exigida pela URL.
   * Sub-rota com resource próprio (`SUBROUTE_FEATURE_MAP`) tem prioridade sobre
   * o segmento base, para que um módulo aninhado não herde a permissão do pai.
   */
  private resolveRouteFeature(url: string): { routeKey: string; feature?: string } {
    const path = this.getPathFromUrl(url);

    for (const [subRoute, feature] of Object.entries(SUBROUTE_FEATURE_MAP)) {
      if (path === subRoute || path.startsWith(`${subRoute}/`)) {
        return { routeKey: subRoute, feature };
      }
    }

    const baseRoute = path.split('/')[0] || '';
    return { routeKey: baseRoute, feature: ROUTE_FEATURE_MAP[baseRoute] };
  }

  /** Caminho da URL sem query/hash e sem a barra inicial. */
  private getPathFromUrl(url: string): string {
    return url.split('?')[0].split('#')[0].replace(/^\//, '');
  }

  private getBaseRouteFromUrl(url: string): string {
    return this.resolveRouteFeature(url).routeKey;
  }
}
